import { Link } from "react-router-dom";
import { Truck, Package, Shield, Clock, Phone, CheckCircle, Users, Award, ThumbsUp, ArrowRight, Star, Building2, Boxes, Zap } from "lucide-react";

const Home = () => {
  const services = [
    {
      icon: <Truck className="w-12 h-12" />,
      title: "Residential Moves",
      description: "Safe, efficient home and apartment removals with full care handling",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      borderColor: "border-blue-200"
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: "Business & Commercial Transport",
      description: "Reliable logistics for offices, retail, and operational equipment",
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      textColor: "text-orange-600",
      borderColor: "border-orange-200"
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Daily Supply & Distribution Services",
      description: "We provide reliable daily delivery services for essential goods including newspapers, milk, tissue products, and other supplies. Our scheduled logistics ensure timely and consistent distribution for businesses and local operations",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      textColor: "text-green-600",
      borderColor: "border-green-200"
    },
    {
      icon: <Boxes className="w-12 h-12" />,
      title: "Light Clearance & Removal Support",
      description: "We assist with general property clearance as part of removals and transport services",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      textColor: "text-red-600",
      borderColor: "border-red-200"
    }
  ];

  const stats = [
    { number: "Trusted", label: "By Clients Across UK" },
    { number: "98%", label: "On-Time Delivery" },
    { number: "100%", label: "Licensed & Insured" },
    { number: "24/7", label: "Customer Support" }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Fast Response Times",
      description: "We respond quickly and get your job moving without delays"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Reliable & Punctual",
      description: "We show up on time and deliver as promised"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Fully Licensed & Insured",
      description: "Your goods are protected at every step"
    },
    {
      icon: <ThumbsUp className="w-8 h-8" />,
      title: "Flexible & Scalable",
      description: "From small moves to large logistics operations"
    }
  ];

  const testimonials = [
    {
      name: "Michael Richardson",
      service: "House Move, London",
      rating: 5,
      text: "Fast, professional, and reliable. Moved my entire 3-bedroom house without a single issue. Highly recommended!"
    },
    {
      name: "Sarah Chen",
      service: "Office Relocation, Manchester",
      rating: 5,
      text: "Maruns handled our office move brilliantly. Everything arrived safely and on schedule. Excellent service!"
    },
    {
      name: "David Thompson",
      service: "Equipment Transport, Birmingham",
      rating: 5,
      text: "Needed sensitive equipment moved urgently. They responded fast and handled everything with care. Will use again!"
    }
  ];

  return (
    <div className="w-full" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-orange-500 text-white py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-green-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <Award className="w-5 h-5 mr-2" />
              <span className="text-sm font-semibold">UK Licensed • Fully Insured • 24/7 Support</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-testid="hero-title">
              Reliable Transport & Logistics Across the UK — On Time, Every Time
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed" data-testid="hero-subtitle">
              From home removals to specialized business logistics and daily supply deliveries, Maruns Limited delivers fast, secure, and fully insured transport solutions you can trust.
            </p>
            <p className="text-base md:text-lg mb-8 text-white/80">
              Trusted by households and businesses across the UK
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="btn-primary bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all text-center inline-flex items-center justify-center shadow-lg"
              >
                Get a Free Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <a
                href="tel:+447405747177"
                data-testid="hero-cta-call"
                className="flex items-center justify-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-purple-600 transition-all shadow-lg"
              >
                <Phone className="w-5 h-5" />
                <span>Call: +44 7405 747177</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 bg-clip-text text-transparent mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Our Logistics Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Professional transport solutions tailored to your needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className={`service-card ${service.bgColor} p-8 rounded-2xl shadow-lg hover:shadow-2xl border-2 ${service.borderColor} group cursor-pointer`}
              >
                <div className={`bg-gradient-to-br ${service.color} text-white p-4 rounded-xl inline-block mb-4 group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${service.textColor}`}>{service.title}</h3>
                <p className="text-gray-700 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              Explore All Services
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Simple. Fast. Reliable.</h2>
            <p className="text-xl text-gray-600">Get your logistics sorted in three easy steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Request a Quote</h3>
              <p className="text-gray-600">Tell us what you need moved</p>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-purple-600 to-orange-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Get a Quick Estimate</h3>
              <p className="text-gray-600">We provide a clear, competitive price</p>
            </div>
            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-600 to-red-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">We Handle the Job</h3>
              <p className="text-gray-600">Our team delivers safely and on time</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-testid="about-heading">
                About Maruns Limited
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Launched in September 2025, Maruns Limited has rapidly become a trusted logistics provider across the UK. We specialize in transportation and moving services for diverse sectors including residential, commercial, food service, clearance, and technology industries.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Our exceptional growth and consistent performance have enabled us to expand through the UK Sponsor License program, bringing skilled logistics professionals to maintain our high standards across all specialized transport services.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Our Vision</h3>
                    <p className="text-gray-600">To be the UK's leading specialized logistics provider, setting new standards in transport services across residential, commercial, and specialized industry sectors.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Our Mission</h3>
                    <p className="text-gray-600">To deliver exceptional logistics and transport services across all sectors through licensed professionals, modern fleet, and unwavering commitment to reliability and customer satisfaction.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/maruns-logo.png"
                alt="Maruns Limited Logo"
                className="rounded-xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="features-heading">
              Why Choose Maruns Limited
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">This is where you sell confidence</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="service-card bg-white p-6 rounded-xl shadow-md hover:shadow-xl border border-gray-100"
              >
                <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-orange-500 text-white p-3 rounded-lg inline-block mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Real feedback from real customers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-blue-100">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="cta-heading">
            Need Reliable Transport Today?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Get a fast quote or speak directly with our team. No obligation quote — we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg"
            >
              Get a Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <a
              href="tel:+447405747177"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-purple-600 transition-all shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call +44 7405 747177
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;